import React, {lazy, Suspense, useEffect, useState} from "react";
import {Outlet, useLocation} from "react-router-dom";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import ProfileSync from "../../features/auth/ProfileSync";
import useMediaQuery from "../../hooks/useMediaQuery";
import "./shell.css";

const CommandPalette = lazy(() => import("./CommandPalette"));

const STORAGE_KEY = "ui.sidebar.collapsed";

const readCollapsed = () => {
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch (error) {
        return false;
    }
};

const AppShell = () => {
    const location = useLocation();
    const isMobile = useMediaQuery("(max-width: 1023px)");
    const [collapsed, setCollapsed] = useState(readCollapsed);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [commandOpen, setCommandOpen] = useState(false);

    useEffect(() => {
        try {
            window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
        } catch (error) {
            // localStorage может быть недоступен в приватном режиме
        }
    }, [collapsed]);

    useEffect(() => {
        const onKeyDown = (event) => {
            const isPalette = (event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k";
            if (isPalette) {
                event.preventDefault();
                setCommandOpen((open) => !open);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        window.scrollTo({top: 0, behavior: "auto"});
    }, [location.pathname]);

    return (
        <div className={`ui-shell${collapsed ? " is-collapsed" : ""}`}>
            <ProfileSync/>
            <a className="skip-link" href="#main-content">Перейти к содержимому</a>

            <Sidebar
                collapsed={isMobile ? false : collapsed}
                mobileOpen={mobileOpen}
                onClose={() => setMobileOpen(false)}
                onOpenCommand={() => setCommandOpen(true)}
            />

            {mobileOpen && (
                <div
                    className="ui-scrim"
                    role="presentation"
                    onClick={() => setMobileOpen(false)}
                />
            )}

            <div className="ui-shell__main">
                <Topbar
                    collapsed={collapsed}
                    onToggleCollapse={() => setCollapsed((value) => !value)}
                    onOpenMobile={() => setMobileOpen(true)}
                    onOpenCommand={() => setCommandOpen(true)}
                />
                <main id="main-content" className="ui-shell__content" tabIndex={-1}>
                    <Outlet/>
                </main>
            </div>

            {commandOpen && (
                <Suspense fallback={null}>
                    <CommandPalette open onClose={() => setCommandOpen(false)}/>
                </Suspense>
            )}

            <ToastContainer
                position="bottom-right"
                autoClose={3200}
                hideProgressBar
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="colored"
            />
        </div>
    );
};

export default AppShell;
